<script setup lang="ts">
import { computed, ref } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useQuery } from '@tanstack/vue-query';
import {
  ArrowLeft,
  Building2,
  FolderOpen,
  Files,
  MapPin,
  NotebookPen,
  Pencil,
  UserRound,
  UsersRound,
} from '@lucide/vue';
import { useAuthStore } from '@/stores/auth';
import { useInvalidation } from '@/composables/useInvalidation';
import { useActiveTab } from '@/composables/useActiveTab';
import { useActiveTabScroll } from '@/composables/useActiveTabScroll';
import StatoErrore from '@/components/ui/StatoErrore.vue';
import PulsanteAzione from '@/components/ui/PulsanteAzione.vue';
import type { Cliente } from '@evolfin/shared';
import FormCliente from '../components/FormCliente.vue';
import AnagraficaScheda from '../components/AnagraficaScheda.vue';
import SediSezione from '../components/SediSezione.vue';
import PersoneSezione from '../components/PersoneSezione.vue';
import NoteSezione from '../components/NoteSezione.vue';
import ReferentiSezione from '../components/ReferentiSezione.vue';
import DocumentiSezione from '../components/DocumentiSezione.vue';
import ProgettiSezione from '@/features/progetti/components/ProgettiSezione.vue';
import { documentiRichiestiMancanti } from '@/api/file';
import { dettaglioCliente } from '../api';
import { coloriStatoCliente } from '../stati';

definePage({ meta: { titolo: 'Clienti' } });

const route = useRoute();
const router = useRouter();
const auth = useAuthStore();
const invalidate = useInvalidation('clienti');

const clienteId = computed(() => Number((route.params as { id: string }).id));
const puoModificare = computed(() => auth.ha('can_write_clienti'));

const {
  data: cliente,
  isLoading,
  isError,
  refetch,
} = useQuery({
  queryKey: ['clienti', clienteId],
  queryFn: () => dettaglioCliente(clienteId.value),
  // È la pagina su cui si lavora: rimontandola si riparte dal server, non da
  // una copia di trenta secondi fa.
  refetchOnMount: 'always',
});

// Stessa chiave della sezione Documenti: un caricamento lì aggiorna anche il conteggio qui.
const { data: richiestiMancanti } = useQuery({
  queryKey: computed(() => ['clienti', clienteId.value, 'file', 'richiesti-mancanti']),
  queryFn: () => documentiRichiestiMancanti(clienteId.value),
});
const nMancanti = computed(() => richiestiMancanti.value?.length ?? 0);

const tabs = [
  { chiave: 'anagrafica', etichetta: 'Anagrafica', icona: Building2 },
  { chiave: 'sedi', etichetta: 'Sedi', icona: MapPin },
  { chiave: 'persone', etichetta: 'Persone', icona: UserRound },
  { chiave: 'note', etichetta: 'Note', icona: NotebookPen },
  { chiave: 'referenti', etichetta: 'Referenti', icona: UsersRound },
  { chiave: 'documenti', etichetta: 'Documenti', icona: Files },
  { chiave: 'progetti', etichetta: 'Progetti', icona: FolderOpen },
] as const;
type ChiaveTab = (typeof tabs)[number]['chiave'];
const tab = useActiveTab(
  tabs.map((t) => t.chiave),
  'anagrafica',
);
useActiveTabScroll(tab);

// Modifica anagrafica in-place sulla scheda del cliente (niente modale).
const modifica = ref(false);
function avviaModifica(): void {
  tab.value = 'anagrafica';
  modifica.value = true;
}
function selezionaTab(chiave: ChiaveTab): void {
  modifica.value = false; // cambiare tab annulla una modifica in corso
  tab.value = chiave;
}
function fineModifica(_cliente: Cliente): void {
  modifica.value = false;
  void invalidate();
}
</script>

<template>
  <div class="flex flex-col gap-6">
    <button
      type="button"
      class="flex w-fit items-center gap-1.5 text-[13px] text-ink-secondary transition-colors hover:text-ink"
      @click="router.push('/clienti')"
    >
      <ArrowLeft :size="15" :stroke-width="1.9" /> Clienti
    </button>

    <div v-if="isLoading" class="text-[13px] text-ink-muted">Caricamento…</div>

    <StatoErrore
      v-else-if="isError || !cliente"
      titolo="Cliente non trovato"
      messaggio="Il cliente richiesto non esiste o non è più disponibile."
      @riprova="refetch()"
    />

    <template v-else>
      <header class="flex flex-wrap items-start justify-between gap-4">
        <div class="flex items-start gap-3">
          <span
            class="grid size-12 shrink-0 place-items-center rounded-lg bg-[var(--surface-accent-soft)] text-[var(--color-primary)]"
            aria-hidden="true"
          >
            <Building2 :size="24" :stroke-width="1.8" />
          </span>
          <div>
            <div class="flex flex-wrap items-center gap-2.5">
              <h1 class="font-display text-[28px] leading-tight font-semibold break-words">
                {{ cliente.ragioneSociale }}
              </h1>
              <span
                class="rounded-pill px-2 py-0.5 text-[11px] font-medium"
                :style="{
                  background: `color-mix(in oklch, ${coloriStatoCliente[cliente.stato] ?? 'var(--color-info)'} 12%, transparent)`,
                  color: coloriStatoCliente[cliente.stato] ?? 'var(--color-info)',
                }"
              >
                {{ cliente.stato }}
              </span>
            </div>
            <p class="mt-0.5 text-[13px] text-ink-secondary">
              {{ cliente.tipologia }} ·
              <span class="tabular-nums">{{ cliente.codiceFiscale }}</span>
              <span v-if="cliente.nomeCommerciale"> · {{ cliente.nomeCommerciale }}</span>
            </p>
          </div>
        </div>
        <PulsanteAzione
          v-if="puoModificare && !modifica"
          variante="contorno"
          @click="avviaModifica"
        >
          Modifica
          <template #icona><Pencil :size="15" :stroke-width="1.9" /></template>
        </PulsanteAzione>
      </header>

      <!-- Barra tab -->
      <nav
        ref="tabBar"
        class="flex gap-1 overflow-x-auto border-b border-line-soft"
        aria-label="Sezioni cliente"
      >
        <button
          v-for="t in tabs"
          :key="t.chiave"
          type="button"
          class="flex items-center gap-1.5 border-b-2 px-3 py-2.5 text-[13px] font-medium whitespace-nowrap transition-colors"
          :aria-current="tab === t.chiave ? 'true' : undefined"
          :class="
            tab === t.chiave
              ? 'border-[var(--color-primary)] text-[var(--color-primary)]'
              : 'border-transparent text-ink-secondary hover:text-ink'
          "
          @click="selezionaTab(t.chiave)"
        >
          <component :is="t.icona" :size="15" :stroke-width="1.8" />
          {{ t.etichetta }}
          <span
            v-if="t.chiave === 'documenti' && nMancanti"
            class="relative rounded-pill bg-[var(--surface-danger-soft)] px-1.5 text-[11px] font-semibold text-[var(--color-danger-text)] tabular-nums"
            :title="`${nMancanti} ${nMancanti === 1 ? 'documento richiesto mancante' : 'documenti richiesti mancanti'}`"
          >
            <span class="sr-only">mancanti:</span> {{ nMancanti }}
          </span>
        </button>
      </nav>

      <section>
        <template v-if="tab === 'anagrafica'">
          <FormCliente
            v-if="modifica"
            :cliente="cliente"
            @salvato="fineModifica"
            @annulla="modifica = false"
          />
          <AnagraficaScheda v-else :cliente="cliente" />
        </template>
        <SediSezione v-else-if="tab === 'sedi'" :cliente-id="cliente.id" />
        <PersoneSezione v-else-if="tab === 'persone'" :cliente-id="cliente.id" />
        <NoteSezione v-else-if="tab === 'note'" :cliente-id="cliente.id" />
        <ReferentiSezione v-else-if="tab === 'referenti'" :cliente-id="cliente.id" />

        <DocumentiSezione v-else-if="tab === 'documenti'" :cliente-id="cliente.id" />
        <ProgettiSezione v-else-if="tab === 'progetti'" :cliente-id="cliente.id" />
      </section>
    </template>
  </div>
</template>
